/**
 * Common page paths within the Portal.
 * These are base paths without route parameters.
 */
export const PagePaths = {
  Home: '/trm',
  Health: '/trm/health',
  NotFound: '/trm/404',
  ServerDown: '/trm/server-down',
  NoAccess: '/trm/noaccess',
  UnknownError: '/trm/unknown-error',
  SignIn: '/trm/sign-in',
  Fallback: '/trm/fallback',
  PublicLoad: '/trm/preview/:tokenId',
  PublicCarrierDetails: '/trm/carrier-details/:carrierToken',
  PublicCarrierFavoriteLanes: '/trm/favorite-lanes/:carrierToken',
  // broker-specific routes
  Broker: {
    Root: '/trm/broker',
    Account: {
      Root: '/trm/broker/account',
      Info: '/trm/broker/account/info',
      Filters: '/trm/broker/account/filters',
      Survey: '/trm/broker/account/survey',
    },
    TruckList: {
      Root: '/trm/broker/trucklist',
    },
    LoadOffers: {
      Root: '/trm/broker/load-offers',
      Booked: '/trm/broker/load-offers/booked',
    },
    PostedLoads: {
      Root: '/trm/broker/posted-loads',
    },
    CarrierProfile: '/trm/broker/carrier-profile/:carrierId',
    LoadboardControlCenter: {
      Root: '/trm/broker/loadboard-control-center',
    },
  },

  // carrier-specific routes
  Carrier: {
    Root: '/trm/carrier',
    SignUp: '/trm/carrier/signup',
    Onboarding: {
      Start: '/trm/carrier/onboarding/start',
      CompanyInfo: '/trm/carrier/onboarding/company-info',
      Equipment: '/trm/carrier/onboarding/equipment',
    },
    Loadboard: '/trm/carrier/loadcentral',
    PostTruck: '/trm/carrier/post-truck',
    Recommendations: '/trm/carrier/recommendations',
  },

  Account: {
    // Keep this around for signout and error pages
    Root: '/trm/account',
    Profile: '/trm/account/profile',
    /** @deprecated Use PagePaths.Broker.Account.Info instead */
    Info: '/trm/account/info',
    /** @deprecated Use PagePaths.Broker.Account.Filters instead */
    Filters: '/trm/account/filters',
    /** @deprecated Use PagePaths.Broker.Account.Survey instead */
    Survey: '/trm/account/survey',
  },

  /** @deprecated Use PagePaths.Broker.TruckList instead */
  TruckList: {
    /** @deprecated Use PagePaths.Broker.TruckList.Root instead */
    Root: '/trm/trucklist',
    /** @deprecated Use PagePaths.Broker.TruckList.Search instead */
    Search: '/trm/trucklist/search',
    /** @deprecated Use PagePaths.Broker.LoadOffers.Root instead */
    LoadOffers: '/trm/trucklist/load-offers',
  },

  /** @deprecated Use PagePaths.Broker.LoadOffers instead */
  LoadOffers: {
    /** @deprecated Use PagePaths.Broker.LoadOffers.Root instead */
    Root: '/trm/load-offers',
    /** @deprecated Use PagePaths.Broker.LoadOffers.Booked instead */
    Booked: '/trm/load-offers/booked',
  },

  /** @deprecated Use PagePaths.Broker.CarrierProfile instead */
  Carriers: {
    /** @deprecated Use PagePaths.Broker.Root instead */
    Root: '/trm/carriers',
    /** @deprecated Use PagePaths.Broker.CarrierProfile instead */
    Profile: '/trm/carriers/:carrierId/profile',
  },
} as const;

// Type for the PagePaths object
export type PagePathsType = typeof PagePaths;

/**
 * Route paths with parameters for React Router.
 * These include route parameters like :tokenId, :loadId, and :carrierId.
 *
 * @param tokenId - Token identifier for public load previews
 * @param loadId - Optional load identifier to pre-select a specific load
 * @param carrierId - Carrier identifier for carrier profile pages
 */
export const ParameterizedPagePaths = {
  PublicLoad: `${PagePaths.PublicLoad}/:loadId?`,
  Carrier: {
    Loadboard: `${PagePaths.Carrier.Loadboard}/:loadId?`,
    Recommendations: `${PagePaths.Carrier.Recommendations}/:loadId?`,
  },
} as const;
